/**
 * Fullscreen helpers for the interview meeting.
 *
 * Running the meeting fullscreen makes the viewport fill the shared screen, so
 * the proctoring overlay's camera-box mapping (utils/proctor-layout) is exact.
 *
 * IMPORTANT ORDERING: request fullscreen only AFTER the screen-share picker has
 * completed. The getDisplayMedia picker dialog cancels/exits fullscreen, so
 * entering fullscreen BEFORE the picker leaves the page windowed. Selecting a
 * display grants transient activation, so a request right after getDisplayMedia
 * resolves is allowed; the "return to fullscreen" button is the reliable
 * fallback for any browser that rejects it or when the candidate presses Esc.
 *
 * All best-effort: a rejection never throws into the caller.
 */
/** True when the page is currently fullscreen via the Fullscreen API. */
export function isDocFullscreen() {
    if (typeof document === 'undefined')
        return false;
    const d = document;
    return !!(document.fullscreenElement || d.webkitFullscreenElement);
}
/**
 * True when the viewport fills the whole screen — this catches NATIVE / F11
 * browser fullscreen, which the Fullscreen API does NOT report
 * (`document.fullscreenElement` stays null and no `fullscreenchange` fires).
 *
 * `innerWidth/innerHeight` and `screen.width/height` are both CSS pixels, so
 * they match (within a rounding tolerance) only when there is no browser chrome
 * — i.e. fullscreen. A merely maximized window is shorter by the chrome height
 * (~90–130px), well beyond the tolerance, so the two are cleanly distinguished.
 */
export function isViewportFullscreen() {
    if (typeof window === 'undefined' || !window.screen)
        return false;
    const sw = window.screen.width;
    const sh = window.screen.height;
    if (!sw || !sh)
        return false;
    const TOL = 3;
    return window.innerWidth >= sw - TOL && window.innerHeight >= sh - TOL;
}
/**
 * True when the page is fullscreen by EITHER the Fullscreen API (the in-app
 * "enter fullscreen" button / requestFullscreen) OR native F11. Use this to
 * decide whether to show the "return to fullscreen for proctoring" prompt, so
 * it doesn't nag a candidate who is already fullscreen via F11, and it updates
 * promptly when they leave (paired with a resize listener — F11 fires resize,
 * not fullscreenchange).
 */
export function isEffectivelyFullscreen() {
    return isDocFullscreen() || isViewportFullscreen();
}
/** Request fullscreen on the whole page (best-effort, gesture-scoped). */
export function enterFullscreen() {
    if (typeof document === 'undefined')
        return;
    if (isDocFullscreen())
        return;
    const el = document.documentElement;
    try {
        const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
        if (req) {
            const r = req.call(el);
            if (r && typeof r.catch === 'function') {
                r.catch(() => undefined);
            }
        }
    }
    catch (_a) {
        /* fullscreen unsupported / rejected — the overlay falls back gracefully */
    }
}
